import { Switch as S } from "radix-ui";
import type { ComponentType, ReactNode, SVGProps } from "react";
import { cn } from "../../lib/cn";

export function Switch({ checked, onCheckedChange, label, disabled, id }: { checked: boolean; onCheckedChange: (v: boolean) => void; label: string; disabled?: boolean; id?: string }) {
  return (
    <S.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className="relative h-5 w-9 shrink-0 rounded-full bg-surface-3 transition-colors duration-150 data-[state=checked]:bg-accent disabled:opacity-50"
    >
      <S.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow transition-transform duration-150 data-[state=checked]:translate-x-[18px]" />
    </S.Root>
  );
}

/** A settings row: title, optional description, control on the right. */
export function SettingRow({ title, description, children, htmlFor }: { title: ReactNode; description?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex items-center justify-between gap-6 py-3">
      <label htmlFor={htmlFor} className="min-w-0">
        <div className="text-sm font-medium text-fg">{title}</div>
        {description && <div className="mt-0.5 text-xs text-fg-3">{description}</div>}
      </label>
      {children}
    </div>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
  size = "md",
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegmentOption<T>[];
  className?: string;
  size?: "sm" | "md";
  ariaLabel: string;
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn("inline-flex rounded-lg bg-surface p-0.5", className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors duration-150",
              size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-sm",
              active ? "bg-surface-3 text-fg shadow-sm" : "text-fg-3 hover:text-fg",
            )}
          >
            {o.icon && <o.icon className="icon size-4" aria-hidden />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Page-level section tabs (profile, community, search). A left-aligned row of
 * quiet chips; the current section gets a filled chip rather than an underline.
 */
export function Tabs<T extends string>({ value, onChange, options, ariaLabel, className }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[]; ariaLabel: string; className?: string }) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn("no-scrollbar flex h-12 items-center gap-1 overflow-x-auto px-3", className)} data-swipe-ignore>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex h-8 shrink-0 items-center rounded-md px-3 text-sm font-medium transition-colors duration-150",
              active ? "bg-surface-2 text-fg" : "text-fg-3 hover:bg-surface hover:text-fg",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Badge({ count, className, dot }: { count?: number; className?: string; dot?: boolean }) {
  if (dot) return <span aria-hidden className={cn("size-2 rounded-full bg-fg", className)} />;
  if (!count) return null;
  return (
    <span className={cn("inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-[11px] font-semibold leading-none text-white tabular", className)}>
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center px-6 py-14 text-center", className)}>
      {Icon && (
        <div className="mb-3 flex size-11 items-center justify-center rounded-xl bg-surface text-fg-2">
          <Icon className="icon size-[22px]" aria-hidden />
        </div>
      )}
      <h3 className="text-base font-semibold text-fg">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-fg-3">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <p className="text-sm text-fg-2">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="h-8 rounded-md bg-surface-2 px-3 text-sm font-medium text-fg hover:bg-surface-3">
          Try again
        </button>
      )}
    </div>
  );
}
